// «Teléfono» del grupo «Nueva venta»: la forma rápida de encontrar un teléfono. Filtros a la
// izquierda y tabla POS; elegir uno abre su detalle y desde ahí arranca la venta en el wizard.
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import { TruncatedNotice } from '@/shared/patterns/TruncatedNotice';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import { usePortalCatalogAll } from './catalog/api/usePortalCatalogAll';
import { PosFiltersPanel } from './pos/components/PosFiltersPanel';
import { PosShell } from './pos/components/PosShell';
import { usePosExplorer } from './pos/usePosExplorer';
import { PhoneSaleDrawer } from './phones/components/PhoneSaleDrawer';
import { PhoneSaleTable } from './phones/components/PhoneSaleTable';
import { catalogReadNotice, isLastPhones, isSellable, phonesForSale } from './phones/lib/phoneSale';
import {
    phonePromotions,
    promotionCount,
    type PhonePromotions,
} from './phones/lib/phonePromotions';

const NO_PROMOTIONS: PhonePromotions = {
    sale: [],
    credit: [],
    saleUnavailable: false,
    undetailed: false,
};

export function SalesPhoneSale() {
    const navigate = useNavigate();
    // El BFF del portal trae, por fila, las promos de venta y de crédito del equipo.
    const catalog = usePortalCatalogAll();
    const phones = useMemo(() => phonesForSale(catalog.data?.items ?? []), [catalog.data]);
    const promotionsBySku = useMemo(
        () =>
            new Map(
                phones.map((phone) => [
                    phone.sku,
                    phonePromotions(phone, catalog.data?.scopeWidePromotions),
                ]),
            ),
        [phones, catalog.data],
    );
    const promotionsOf = useCallback(
        (item: CatalogItem): PhonePromotions => promotionsBySku.get(item.sku) ?? NO_PROMOTIONS,
        [promotionsBySku],
    );
    // Filtro, píldora y estadística cuentan las mismas promos que la celda y el cajón.
    const isPromoted = useCallback(
        (item: CatalogItem): boolean => {
            const promotions = promotionsOf(item);
            return promotionCount(promotions) > 0 || promotions.undetailed;
        },
        [promotionsOf],
    );
    const pos = usePosExplorer(phones, isLastPhones, isPromoted);
    const notice = catalogReadNotice(catalog.data, catalog.failedMidway);
    const [detailSku, setDetailSku] = useState<string | null>(null);
    const detailItem = phones.find((phone) => phone.sku === detailSku) ?? null;

    const sell = (sku: string): void => {
        void navigate({ to: '/wizard', search: { sku } });
    };

    return (
        <ScreenFrame>
            <PosShell
                filtersCollapsed={pos.filtersCollapsed}
                filters={
                    <PosFiltersPanel
                        filters={pos.filters}
                        options={pos.options}
                        onChange={pos.setFilters}
                        onSubmitSearch={() => {
                            const first = pos.rows.find(isSellable);
                            if (first) setDetailSku(first.sku);
                        }}
                        searchRef={pos.searchRef}
                        searchLabel="Buscar teléfono"
                        collapsed={pos.filtersCollapsed}
                        onToggleCollapsed={pos.toggleFiltersCollapsed}
                    />
                }
            >
                {notice === null ? null : (
                    <div className="flex-none px-3 pt-2.5">
                        <TruncatedNotice title={notice.title} body={notice.body} />
                    </div>
                )}
                <PhoneSaleTable
                    rows={pos.rows}
                    stats={pos.stats}
                    stat={pos.stat}
                    onStatChange={pos.setStat}
                    sort={pos.sort}
                    onSortToggle={pos.toggleSort}
                    onOpenDetail={setDetailSku}
                    selectedSku={detailSku}
                    promotionsOf={promotionsOf}
                    isLoading={catalog.isLoading}
                    isError={catalog.isError}
                    onRetry={catalog.refetch}
                />
            </PosShell>
            <PhoneSaleDrawer
                item={detailItem}
                catalog={phones}
                scopeWidePromotions={catalog.data?.scopeWidePromotions}
                onClose={() => {
                    setDetailSku(null);
                }}
                onSell={sell}
            />
        </ScreenFrame>
    );
}
